import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { useExportOptions } from '@/hooks/useExportOptions';

import type { useGetCollectionTableProps } from './config';
import { buildEnterpriseRiskRegisterToolbarConfig } from './registerToolbarConfig';
import type { EnterpriseRiskRegisterFields } from './types';

interface Props {
  title: string;
  /** Already-formatted count display string (e.g. `getCounter(...)` → `(12)`). */
  counter?: string;
  tableProps: ReturnType<typeof useGetCollectionTableProps>;
  getActiveRibbonFilters: () => FilterModal[];
  /** Navigates to the create-enterprise-risk page (owned by the page). */
  onCreate: () => void;
  /** Opens the instantiate modal for the currently selected register rows. */
  onInstantiate: () => void;
  /** true → no register rows selected, so instantiate is disabled (parity). */
  instantiateDisabled: boolean;
}

/**
 * Object toolbar for the Enterprise risk register, mounted only when the
 * `object_toolbar` flag is on. Owning `useExportOptions` here (rather than on the
 * page) keeps it off the legacy `ExportButton` path — with the flag off this
 * component never renders, so the export options are computed once, by whichever
 * path is actually shown (CUS-167, mirroring the Actions/Indicators modules).
 */
export const EnterpriseRiskRegisterToolbar: FC<Props> = ({
  title,
  counter,
  tableProps,
  getActiveRibbonFilters,
  onCreate,
  onInstantiate,
  instantiateDisabled,
}) => {
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'enterpriseRisks',
  });
  const [showPdfModal, setShowPdfModal] = useState(false);
  // Ribbon "Edit Filters" opener bridged up from CustomisableRibbon (relocate
  // mode). Present only when the user can manage the ribbon; drives the toolbar's
  // overflow "Edit" group.
  const ribbonConfig = useObjectToolbarFormStore((s) => s.ribbonConfigState);
  const onShowCustomPdfModal = useCallback(() => setShowPdfModal(true), []);

  const { exportOptions, exportToPdf } =
    useExportOptions<EnterpriseRiskRegisterFields>({
      tableProps,
      entityLabel: title,
      getActiveRibbonFilters,
      onShowCustomPdfModal,
    });

  const toolbarConfig = useMemo(
    () =>
      buildEnterpriseRiskRegisterToolbarConfig({
        title,
        counter,
        exportOptions,
        create: { label: st('createNewButton'), onClick: onCreate },
        instantiate: {
          label: st('instantiateButton'),
          onClick: onInstantiate,
          disabled: instantiateDisabled,
        },
        editRibbon: ribbonConfig
          ? {
              groupLabel: t('edit'),
              label: t('objectToolbar.filterSettings'),
              onClick: ribbonConfig.openEditRibbon,
            }
          : undefined,
      }),
    [
      title,
      counter,
      exportOptions,
      onCreate,
      onInstantiate,
      instantiateDisabled,
      ribbonConfig,
      st,
      t,
    ]
  );

  return (
    <>
      <ObjectToolbar config={toolbarConfig} />
      <CustomPdfExportModal
        exportToPdf={async (options) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={title}
        showModal={showPdfModal}
        setShowModal={setShowPdfModal}
        showRibbonOptions={true}
      />
    </>
  );
};
